import React, { useState } from 'react';
import { Modal, Button,  FlashMessage } from '../../components/common';
import httpRequest from '../../utils/httpRequest';
import API_URLS from '../../utils/apiUrls';
import LoopIcon from '@material-ui/icons/Loop';

interface SEOAIGeneratorProps {
  visible: boolean;
  onClose: () => void;
  onSelectContent: (content: string) => void;
  type: 'title' | 'description';
  currentContent?: string;
  focusKeyword?: string;
  articleTitle?: string;
}

interface GeneratedContent {
  content: string;
  id: string;
}

const SEOAIGenerator: React.FC<SEOAIGeneratorProps> = ({
  visible,
  onClose,
  onSelectContent,
  type,
  currentContent = '',
  focusKeyword = '',
  articleTitle = ''
}) => {
  const [prompt, setPrompt] = useState('');
  const [suggestions, setSuggestions] = useState<GeneratedContent[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);

  // Configuration based on type
  const config = {
    title: {
      modalTitle: 'Generate SEO Title With AI',
      apiUrl: API_URLS.getSeoTitleSuggestions,
      apiParam: 'titles',
         characterLimit: 60,
      suggestionTitle: 'Title Suggestions',
      className: 'title-text'
    },
    description: {
      modalTitle: 'Generate SEO Description With AI',
      apiUrl: API_URLS.getSeoDescption,
      apiParam: 'descriptions',
      characterLimit: 160,
      suggestionTitle: 'Description Suggestions',
      className: 'description-text'
    }
  };

  const currentConfig = config[type];

   const generateContent = async (userPrompt: string): Promise<GeneratedContent[]> => {
    // pass current content to avoid duplicates on regenerate
    const exclude = suggestions.map(s => s.content);
    const res = await httpRequest().get(currentConfig.apiUrl, {
      params: { prompt: userPrompt, exclude }
    });

    const contents: string[] = res?.data?.data?.[currentConfig.apiParam] || [];
    return contents.map((content, index) => ({
      content,
      id: `${type}-${Date.now()}-${index}`
    }));
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) return;

    setIsGenerating(true);
    try {
      const generated = await generateContent(prompt);
      setSuggestions(generated);
      setHasGenerated(true);
      FlashMessage(`Generated SEO ${type} suggestions.`, 'success');
    } catch (error) {
      console.error(`Error generating ${type}s:`, error);
      FlashMessage(`Failed to generate ${type}s. Please try again.`, 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerate = async () => {
    setIsGenerating(true);
    try {
      const generated = await generateContent(prompt);
      setSuggestions(generated);
      FlashMessage('Regenerated new suggestions.', 'success');
    } catch (error) {
      console.error(`Error regenerating ${type}s:`, error);
      FlashMessage('Failed to regenerate. Please try again.', 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUseContent = (content: string) => {
    onSelectContent(content);
    onClose();
    // Reset state
    setPrompt('');
    setSuggestions([]);
    setHasGenerated(false);
  };

  const handleClose = () => {
    onClose();
    // Reset state
    setPrompt('');
    setSuggestions([]);
    setHasGenerated(false);
  };

  // Get character count class for styling
  const getCharacterCountClass = (length: number) => {
    const limit = currentConfig.characterLimit;
    if (length > limit) return 'error';
    if (length > limit * 0.9) return 'warning'; // 90% of limit
    return '';
  };

  // Auto-populate prompt when modal opens
  React.useEffect(() => {
    if (visible && (focusKeyword || articleTitle)) {
           // eslint-disable-next-line
      const keyword = focusKeyword || articleTitle;
     }
  }, [visible, focusKeyword, articleTitle, type]);

  // Reset state when type changes
  React.useEffect(() => {
    setPrompt('');
    setSuggestions([]);
    setHasGenerated(false);
  }, [type]);

  return (
    <Modal
      visible={visible}
      onClose={handleClose}
      className="seo-ai-generator-modal"
      showCloseButton={false}
      title={currentConfig.modalTitle}
    >
      <div className="ai-generator-container">
        <div className="ai-generator-content">
          <div className="prompt-section">
            <label className="prompt-label">Enter Your AI Prompt</label>
            <textarea
              className="ai-prompt-textarea"
              placeholder="Type your prompt here..."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={4}
            />
          </div>

          {!hasGenerated && (
            <div className="generate-button-section">
              <Button
                onClick={handleGenerate}
                disabled={!prompt.trim() || isGenerating}
                loading={isGenerating}
                className="generate-ai-btn"
              >
                {isGenerating ? 'Generating...' : 'Generate AI'}
              </Button>
            </div>
          )}

          {hasGenerated && (
            <div className="suggestions-section">
              <div className="suggestions-header">
                <h4 className="suggestions-title">{currentConfig.suggestionTitle}</h4>
              </div>

              <div className="suggestions-list">
                {suggestions.map((suggestion) => (
                  <div key={suggestion.id} className="suggestion-item">
                    <div className={`suggestion-text ${currentConfig.className}`}>
                      {suggestion.content}
                      {type === 'description' && (
                        <span 
                          className={`character-count-badge ${getCharacterCountClass(suggestion.content.length)}`}
                        >
                          {suggestion.content.length}/{currentConfig.characterLimit}
                        </span>
                      )}
                    </div>
                    {type === 'title' && (
                      <div className="title-character-count">
                        <span className={`character-count ${getCharacterCountClass(suggestion.content.length)}`}>
                          {suggestion.content.length}/{currentConfig.characterLimit}
                        </span>
                      </div>
                    )}
                    <Button
                      className="use-text-btn"
                      onClick={() => handleUseContent(suggestion.content)}
                      size="small"
                      color="secondary"
                    >
                      Use Text
                    </Button>
                  </div>
                ))}
              </div>

              <div className="regenerate-section">
                <Button
                  onClick={handleRegenerate}
                  disabled={isGenerating}
                  loading={isGenerating}
                  className="regenerate-btn"
                  color="primary"
                >
                  <LoopIcon />
                  {isGenerating ? 'Regenerating...' : 'Regenerate'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default SEOAIGenerator;